import {
  Box,
  Button,
  Popover,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import React from 'react';
import { useStore } from 'zustand';
import { useHandleSetCreateStates } from '../hooks/useHandleSetCreateStates';
import { useHandleGlobalStateState } from '../hooks/useHandleGlobalState';
import { useUnityBuildStore } from '../stores/store';
import { UserPicture } from './UserPicture';
import { Settings } from './settings/Settings';
import LogIn from './LogIn';
import { useFetchUser } from '../hooks/useFetchUsers';
import { useAssemblyStore } from '../stores/AssemblyStore';
import CreateAssembly from './assembliesAndItems/assemblies/CreateAssembly';
import { GlobalAssembly } from '../api/protosCompiled/globalAssembly/globalAssembly_pb';
import { getContrastTextColor } from '../utils/getCintrastTextColor';
import chatBotImage from '../images/ChatBot.webp';
import { DarkModeToggle } from './DarkModeToggle';

// Import MUI Icons
import CategoryIcon from '@mui/icons-material/Category';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import HomeIcon from '@mui/icons-material/Home';
import LoginIcon from '@mui/icons-material/Login';
import { useLocalStorage } from '../stores/localStorage';

// Import responsive utilities
import { useResponsive, ResponsiveHelpers } from '../theme';
import { Z_INDEX_LAYERS } from '../utils/zIndexLayers';

export function MainMenu() {
  const theme = useTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile, isTablet } = responsive;

  const isFetchingToken = useStore(useLocalStorage).isFetchingToken;
  const isLoggedIn = useStore(useUnityBuildStore).isLoggedIn;
  const selectedProject = useStore(useUnityBuildStore).selectedProject;
  const isOpeningViewport = useStore(useUnityBuildStore).isOpeningViewport;
  const isViewingExtension = useStore(useUnityBuildStore).isViewingExtension;
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedContract = useStore(useUnityBuildStore).selectedContract;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const setIsViewingExtension = useStore(useUnityBuildStore).setIsViewingExtension;

  const assemblyOption = useStore(useAssemblyStore).assemblyOption;
  const currentGlobalAssembly = useStore(useAssemblyStore).currentGlobalAssembly;
  const isViewingAssemblyForm = useStore(useAssemblyStore).isViewingAssemblyForm;
  const setIsViewingAssemblyForm = useStore(useAssemblyStore).setIsViewingAssemblyForm;
  const setAssemblyOption = useStore(useAssemblyStore).setAssemblyOption;
  const setIsCreatingAssembly = useStore(useAssemblyStore).setIsCreatingAssembly;

  const { data: user, isLoading: isLoadingUser } = useFetchUser();

  const handleSetGlobalState = useHandleGlobalStateState();
  const handleSetCreateStates = useHandleSetCreateStates();

  const setIsLoggedIn = useUnityBuildStore().setIsLoggedIn;
  const setProjectListIsMounted = useUnityBuildStore().setProjectListIsMounted;

  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);

  const handleOpenLoginDialog = () => {
    //check local storage for session token
    const sessionToken = localStorage.getItem('sessionToken');
    if (sessionToken) {
      setIsLoggedIn(true);
      setProjectListIsMounted(true);
    } else {
      setLoginDialogOpen(true);
    }
  };

  const handleCloseLoginDialog = () => {
    setLoginDialogOpen(false);
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const handleOpenSettings = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'settings-popover' : undefined;

  // Determine text color based on background or theme
  const getTextColor = () => {
    if (theme.palette.mode === 'dark') {
      return getContrastTextColor(theme.palette.background.paper);
    }
    return theme.palette.text.primary;
  };

  return (
    <AppBar
      position="static"
      color="default"
      elevation={2}
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: getTextColor(),
        zIndex: 1000,
        borderBottom: `1px solid ${alpha(getTextColor(), 0.09)}`,
        boxShadow: theme.shadows[1],
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          justifyContent: 'space-between',
          minHeight: { xs: '64px', sm: '58px' },
          px: { xs: 0.5, sm: 1 },
          pb: 0.5,
          flexDirection: 'row',
          zIndex: Z_INDEX_LAYERS.UI_COMPONENTS.TOOLBAR_CONTAINER,
        }}
      >
        {/* Render CreateAssembly when currentTakeoffAssembly is set and menuOption is "Edit Assembly" */}
        {isViewingAssemblyForm && assemblyOption === 'global' && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100vh',
              backgroundColor: theme.palette.background.default,
              color: getTextColor(),
              zIndex: 1300,
              display: 'flex',
              overflow: 'hidden',
            }}
            role="dialog"
            aria-modal="true"
            aria-label="Create or edit assembly"
          >
            <CreateAssembly
              assemblyOption="global"
              currentAssembly={currentGlobalAssembly as GlobalAssembly.AsObject}
            />
          </Box>
        )}

        {isLoggedIn ? (
          <>
            {/* Left Section: User and Navigation */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto',
                mb: isMobile ? 1 : 0,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: isMobile ? 'space-between' : 'flex-start',
                }}
              >
                {isLoadingUser ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <Tooltip title="User settings" arrow placement="bottom">
                    <IconButton
                      onClick={handleOpenSettings}
                      aria-label="Open settings menu"
                      size="small"
                      sx={{
                        p: 0.5,
                        mr: { xs: 0, sm: 1 },
                        border: `1px solid ${alpha(getTextColor(), 0.3)}`,
                        '&:hover': {
                          backgroundColor: alpha(getTextColor(), 0.08),
                        },
                      }}
                    >
                      {!isLoadingUser && user && user?.user?.userphotourl ? (
                        <UserPicture user={user as any} />
                      ) : (
                        <Box
                          component="img"
                          src={chatBotImage}
                          alt=""
                          aria-hidden="true"
                          sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: `1px solid ${alpha(getTextColor(), 0.3)}`,
                          }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                )}

                <DarkModeToggle />
              </Box>

              <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleCloseSettings}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: {
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    mt: 0.5,
                    boxShadow: theme.shadows[4],
                    maxWidth: { xs: '95vw', sm: 'auto' },
                    maxHeight: { xs: '80vh', sm: 'auto' },
                    overflowY: 'auto',
                    '& .MuiTypography-root': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiFormLabel-root': {
                      color:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.7)
                          : alpha(theme.palette.common.black, 0.6),
                    },
                    '& .MuiInputBase-root': {
                      color: theme.palette.text.primary,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.common.white, 0.2)
                          : alpha(theme.palette.common.black, 0.23),
                    },
                    '& .MuiMenuItem-root': {
                      color: theme.palette.text.primary,
                    },
                  },
                }}
              >
                <Settings
                  onCloseMenu={handleCloseSettings}
                  onLogout={() => setLoginDialogOpen(false)}
                />
              </Popover>

              <Tooltip title="Items and assemblies management" arrow>
                <Button
                  onClick={() =>
                    handleSetGlobalState({
                      resetOption: 'resetAssembly',
                      setStateFunctions: [
                        {
                          setStateFunction: setIsViewingAssemblyForm,
                          prop: true,
                        },
                        {
                          setStateFunction: setIsCreatingAssembly,
                          prop: true,
                        },
                        {
                          setStateFunction: setAssemblyOption,
                          prop: 'global',
                        },
                      ],
                    })
                  }
                  variant="outlined"
                  color="inherit"
                  startIcon={<CategoryIcon aria-hidden="true" />}
                  aria-label="Manage items and assemblies"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderColor: alpha(getTextColor(), 0.5),
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: getTextColor(),
                      backgroundColor: alpha(getTextColor(), 0.1),
                    },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  {isMobile && !isTablet ? 'Items' : 'Items & Assemblies'}
                </Button>
              </Tooltip>
            </Box>

            {/* Center/Right Section: Navigation and Info */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.5, sm: 1 },
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile ? '100%' : 'auto',
              }}
            >
              {selectedProject.id && (
                <Tooltip title="Return to project list" arrow>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size={isMobile ? 'small' : 'medium'}
                    startIcon={<HomeIcon aria-hidden="true" />}
                    aria-label="Return to project list"
                    sx={{
                      borderColor: alpha(getTextColor(), 0.5),
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: getTextColor(),
                        backgroundColor: alpha(getTextColor(), 0.1),
                      },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      width: isMobile ? '100%' : 'auto',
                    }}
                    onClick={() =>
                      handleSetGlobalState({
                        resetOption: 'returnHome',
                      })
                    }
                  >
                    Project List
                  </Button>
                </Tooltip>
              )}

              {selectedProject.id && (
                <>
                  {(selectedEstimate?.estimateid ||
                    selectedChangeOrder?.changeorderid ||
                    selectedContract?.contractid) && (
                    <Tooltip title="Return to takeoff lists" arrow>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<ListAltIcon aria-hidden="true" />}
                        aria-label="Return to takeoff lists"
                        sx={{
                          borderColor: alpha(getTextColor(), 0.5),
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: getTextColor(),
                            backgroundColor: alpha(getTextColor(), 0.1),
                          },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          width: isMobile ? '100%' : 'auto',
                        }}
                        onClick={() =>
                          handleSetGlobalState({
                            resetOption: 'returnToTakeoffList',
                          })
                        }
                      >
                        Takeoff Lists
                      </Button>
                    </Tooltip>
                  )}

                  {isOpeningViewport && (
                    <Tooltip title="Open closeout view" arrow>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size={isMobile ? 'small' : 'medium'}
                        startIcon={<AssignmentReturnedIcon aria-hidden="true" />}
                        aria-label="Open closeout view"
                        sx={{
                          borderColor: alpha(getTextColor(), 0.5),
                          textTransform: 'none',
                          '&:hover': {
                            borderColor: getTextColor(),
                            backgroundColor: alpha(getTextColor(), 0.1),
                          },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          width: isMobile ? '100%' : 'auto',
                        }}
                        onClick={() =>
                          handleSetCreateStates(setIsViewingExtension, !isViewingExtension)
                        }
                      >
                        Closeout
                      </Button>
                    </Tooltip>
                  )}

                  {/* Project/Estimate Info */}
                  {!isMobile && (
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        mx: 1,
                        backgroundColor: alpha(getTextColor(), 0.2),
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexDirection: isMobile ? 'column' : 'row',
                      width: isMobile ? '100%' : 'auto',
                      mt: isMobile ? 1 : 0,
                    }}
                  >
                    {selectedProject.name && (
                      <Chip
                        label={
                          isMobile ? `${selectedProject.name}` : `Project: ${selectedProject.name}`
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: alpha(getTextColor(), 0.3),
                          color: getTextColor(),
                          '& .MuiChip-label': {
                            px: 1,
                          },
                          width: isMobile ? '100%' : 'auto',
                        }}
                      />
                    )}
                    {selectedEstimate?.name && (
                      <Chip
                        label={
                          isMobile
                            ? `${selectedEstimate.name}`
                            : `Estimate: ${selectedEstimate.name}`
                        }
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: alpha(getTextColor(), 0.3),
                          color: getTextColor(),
                          '& .MuiChip-label': {
                            px: 1,
                          },
                          width: isMobile ? '100%' : 'auto',
                        }}
                      />
                    )}
                  </Box>
                </>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            {isFetchingToken ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenLoginDialog}
                startIcon={<LoginIcon aria-hidden="true" />}
                aria-label="Log in to your account"
                sx={{
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                  },
                }}
              >
                Login 1
              </Button>
            )}
            <Dialog
              open={loginDialogOpen}
              onClose={handleCloseLoginDialog}
              aria-labelledby="login-dialog-title"
              PaperProps={{
                sx: {
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  maxWidth: { xs: '95%', sm: '400px' },
                  width: '100%',
                  margin: { xs: '16px', sm: 'auto' },
                  '& .MuiInputBase-root': {
                    color: theme.palette.text.primary,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.15)
                        : theme.palette.background.paper,
                  },
                  '& .MuiInputLabel-root': {
                    color:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.7)
                        : alpha(theme.palette.common.black, 0.6),
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.2)
                        : alpha(theme.palette.common.black, 0.23),
                  },
                  '& .MuiFormHelperText-root': {
                    color:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.common.white, 0.6)
                        : alpha(theme.palette.common.black, 0.6),
                  },
                },
              }}
            >
              <DialogContent>
                <LogIn />
              </DialogContent>
            </Dialog>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
